/* Card designer — the front of the card, made by the customer, in step 4.
 *
 * This is the template half of "upload your own". The plain photo panel that
 * mb-flow.js renders prints the customer's photograph as the whole card front.
 * A template instead says where the photograph sits, what shape it is cut to,
 * and where their words go on top of artwork we drew — which is the difference
 * between a printed snapshot and a card.
 *
 * mb-flow.js is not modified and does not know this exists. The join is the
 * file input it already owns: this script composites the finished card to a
 * canvas, wraps the JPEG in a File, sets #mb-photo-in.files through a
 * DataTransfer and fires 'change'. To the flow that is indistinguishable from
 * the customer having chosen a file themselves, so setPhoto(), the preview,
 * validate() and the multipart cart add all keep working with no special case
 * in any of them. assets/mb-ai-handoff.js established that trick; this is the
 * same join, done on the same page instead of across two.
 *
 * Consequences of going through the file input, all of them deliberate:
 *
 *   - The design reaches the bakery as properties[Your Image] on the card
 *     line, exactly like every other photo card. Nothing downstream — cart,
 *     order, the ops render — needs to know templates exist.
 *   - setPhoto() rejects anything over max_mb, so the encoder below has to
 *     land under it. See the quality ladder in encode().
 *   - validate() at step 4 only checks that S.photo is set. It cannot know
 *     whether the file is the CURRENT design, so if the customer edits and
 *     immediately hits Continue we would send the previous composite. The
 *     capture-phase gate at the bottom is what stops that.
 *
 * The product needs the image_tag (default 'custom-image') for the flow to
 * append the file at all, and must NOT be tagged 'ai-card', which hides the
 * panel this replaces. Both are checked below and the designer bows out
 * quietly rather than half-appearing.
 */
(function () {
  'use strict';

  var host = document.getElementById('mb-photo');
  var input = document.getElementById('mb-photo-in');
  var cfg = document.querySelector('[data-mb-design-json]');
  if (!host || !input || !cfg) return;
  if (!window.indexedDB && typeof DataTransfer === 'undefined') return;

  var T;
  try { T = JSON.parse(cfg.textContent); } catch (e) { return; }
  if (!T) return;

  /* The artwork URL is resolved in Liquid and passed on the tag, not baked
     into the spec — see the note at the top of snippets/mb-design.liquid. */
  T.artwork = cfg.getAttribute('data-artwork') || T.artwork;
  T.preview = cfg.getAttribute('data-preview') || T.preview;

  if (!T.artwork || !T.slots || !T.slots.length) return;
  if (!T.print_w || !T.print_h) return;

  /* Read-only peek at the flow's own config. Re-parsed rather than shared,
     because D is private to mb-flow.js's IIFE — mb-delivery.js sets the
     precedent for doing exactly this. */
  var F = { max_mb: 15, needs_image: true, is_ai: false };
  try {
    var fb = document.querySelector('[data-mb-flow-json]');
    if (fb) {
      var raw = JSON.parse(fb.textContent);
      if (raw) {
        if (typeof raw.max_mb === 'number') F.max_mb = raw.max_mb;
        F.needs_image = !!raw.needs_image;
        F.is_ai = !!raw.is_ai;
      }
    }
  } catch (e) { /* leave the defaults; the guard below still holds */ }

  /* Without the tag the flow never appends the file, so a finished design
     would silently not reach the order. Better to leave the page alone. */
  if (!F.needs_image || F.is_ai) return;

  /* ------------------------------------------------------------ constants */

  /* One megabyte under the flow's own ceiling. The gap is for the fact that
     toBlob's output size is not knowable in advance — the ladder in encode()
     re-tries downwards, and this is the headroom it aims into. */
  var MAX_BYTES = Math.max(4, F.max_mb - 1) * 1024 * 1024;

  /* Below this the lettering starts to soften at A5, so the ladder stops
     dropping resolution and drops quality instead. */
  var MIN_WIDTH = 2200;

  /* How long after the last keystroke or drag before the print file is
     rebuilt. Long enough that typing a name is not 12 composites, short
     enough that Continue is almost never actually blocked. */
  var SETTLE = 450;

  var UPLOAD_MAX = F.max_mb * 1024 * 1024;

  /* The faces a template may ask for. Kept here rather than in the template
     so a card drawn a year ago still renders in whatever the current stack
     is, and so the builder and the designer cannot disagree about what
     'handwritten' means. */
  var FONTS = {
    handwritten: { stack: '"Caveat","Segoe Script","Bradley Hand",cursive', weight: 700 },
    marker:      { stack: '"Permanent Marker","Segoe Script",cursive', weight: 400 },
    classic:     { stack: '"Playfair Display",Georgia,"Times New Roman",serif', weight: 700 },
    modern:      { stack: 'Inter,Helvetica,Arial,sans-serif', weight: 700 },
    rounded:     { stack: '"Baloo 2",Inter,Helvetica,Arial,sans-serif', weight: 700 },
    typewriter:  { stack: '"Courier Prime","Courier New",Courier,monospace', weight: 700 }
  };

  function font(key) { return FONTS[key] || FONTS.modern; }

  /* ---------------------------------------------------------------- state */

  var S = {
    art: null,      /* Image — the card artwork */
    slots: [],      /* one per template slot: {img, zoom, ox, oy, err} */
    text: [],       /* one string per template text line */
    settle: null,   /* debounce timer */
    busy: false,    /* a composite is running */
    stale: true,    /* the injected file is older than the current design */
    pass: false     /* the next #mb-next click is ours, let it through */
  };

  T.slots.forEach(function () {
    S.slots.push({ img: null, zoom: 1, ox: 0, oy: 0 });
  });
  T.text = T.text || [];
  T.text.forEach(function (l) { S.text.push(l.default || ''); });

  /* ------------------------------------------------------------------ dom */

  var el = {};

  function build() {
    /* The plain panel is hidden, not removed: #mb-photo-in lives inside it
       and is the delivery channel. Hiding the label hides the input with it,
       which is fine — the input is never clicked again, only fed. */
    ['.mb-photo__drop', '.mb-photo__row', '.mb-photo__hint', '.mb-photo__sub']
      .forEach(function (sel) {
        var n = host.querySelector(sel);
        if (n) n.className += ' mbd-off';
      });
    var oldH = host.querySelector('h3');
    if (oldH) oldH.className += ' mbd-off';

    var wrap = document.createElement('div');
    wrap.className = 'mbd';

    var h = document.createElement('h3');
    h.textContent = T.heading || 'Design the front';
    wrap.appendChild(h);

    var sub = document.createElement('p');
    sub.className = 'mbd__sub';
    sub.textContent = T.sub || 'Add your photo and your words. This is what we print.';
    wrap.appendChild(sub);

    /* preview */
    var pv = document.createElement('div');
    pv.className = 'mbd__pv';
    el.cv = document.createElement('canvas');
    el.cv.className = 'mbd__cv';
    pv.appendChild(el.cv);
    el.wait = document.createElement('div');
    el.wait.className = 'mbd__wait';
    el.wait.textContent = 'Getting your card ready…';
    el.wait.hidden = true;
    pv.appendChild(el.wait);
    wrap.appendChild(pv);

    /* slots */
    T.slots.forEach(function (slot, i) { wrap.appendChild(slotUI(slot, i)); });

    /* text lines */
    T.text.forEach(function (line, i) { wrap.appendChild(lineUI(line, i)); });

    el.err = document.createElement('p');
    el.err.className = 'mbd__err';
    el.err.hidden = true;
    wrap.appendChild(el.err);

    host.insertBefore(wrap, host.firstChild);
    host.hidden = false;
  }

  function slotUI(slot, i) {
    var box = document.createElement('div');
    box.className = 'mbd__slot';

    var lab = document.createElement('span');
    lab.className = 'mbd__lab';
    lab.textContent = slot.label || (T.slots.length > 1 ? 'Photo ' + (i + 1) : 'Your photo');
    box.appendChild(lab);

    var well = document.createElement('label');
    well.className = 'mbd__well';

    var fin = document.createElement('input');
    fin.type = 'file';
    fin.accept = 'image/jpeg,image/png,image/webp,image/heic';
    fin.hidden = true;
    well.appendChild(fin);

    var ph = document.createElement('span');
    ph.className = 'mbd__ph';
    ph.textContent = '+  Choose a photo';
    well.appendChild(ph);

    var crop = document.createElement('canvas');
    crop.className = 'mbd__crop';
    well.appendChild(crop);
    box.appendChild(well);

    var ctl = document.createElement('div');
    ctl.className = 'mbd__ctl';
    ctl.hidden = true;
    var zoom = document.createElement('input');
    zoom.type = 'range';
    zoom.className = 'mbd__zoom';
    zoom.min = '1'; zoom.max = '3'; zoom.step = '0.01'; zoom.value = '1';
    zoom.setAttribute('aria-label', 'Zoom this photo');
    ctl.appendChild(zoom);
    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'mbd__rm';
    rm.textContent = 'Remove';
    ctl.appendChild(rm);
    box.appendChild(ctl);

    var tip = document.createElement('p');
    tip.className = 'mbd__tip';
    tip.textContent = 'Drag to move it, and use the slider to zoom. JPG, PNG or WebP up to ' + F.max_mb + 'MB.';
    box.appendChild(tip);

    var st = S.slots[i];
    st.el = { well: well, ph: ph, crop: crop, ctl: ctl, zoom: zoom, file: fin };

    fin.addEventListener('change', function () {
      if (fin.files && fin.files[0]) pick(i, fin.files[0]);
    });

    /* Drag and drop, same behaviour as the panel this replaces. The label
       swallows the click for the file input; these only add the drop. */
    ['dragenter', 'dragover'].forEach(function (ev) {
      well.addEventListener(ev, function (e) {
        e.preventDefault(); well.classList.add('is-drag');
      });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      well.addEventListener(ev, function () { well.classList.remove('is-drag'); });
    });
    well.addEventListener('drop', function (e) {
      e.preventDefault();
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) pick(i, f);
    });

    zoom.addEventListener('input', function () {
      st.zoom = parseFloat(zoom.value) || 1;
      clampOffset(i);
      paintCrop(i); touch();
    });

    rm.addEventListener('click', function (e) {
      /* The button is inside the <label>, so without this the click also
         re-opens the file picker it just cleared. */
      e.preventDefault(); e.stopPropagation();
      st.img = null; st.zoom = 1; st.ox = 0; st.oy = 0;
      st.el.file.value = '';
      well.classList.remove('is-set');
      ph.hidden = false; ctl.hidden = true; zoom.value = '1';
      touch();
    });

    dragable(i);
    return box;
  }

  function lineUI(line, i) {
    var box = document.createElement('div');
    box.className = 'mbd__line';

    var lab = document.createElement('label');
    lab.className = 'mbd__lab';
    lab.textContent = line.label || 'Your words';
    box.appendChild(lab);

    var inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'mbd__in';
    inp.value = S.text[i] || '';
    inp.placeholder = line.placeholder || '';
    if (line.max) inp.maxLength = line.max;
    lab.setAttribute('for', 'mbd-t' + i);
    inp.id = 'mbd-t' + i;
    box.appendChild(inp);

    var cnt = document.createElement('span');
    cnt.className = 'mbd__count';
    box.appendChild(cnt);

    function count() {
      if (!line.max) { cnt.textContent = ''; return; }
      var left = line.max - inp.value.length;
      cnt.textContent = left + ' left';
      cnt.classList.toggle('is-full', left <= 0);
    }
    count();

    inp.addEventListener('input', function () {
      S.text[i] = inp.value;
      count();
      paint(); touch();
    });

    return box;
  }

  /* --------------------------------------------------------------- images */

  function load(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      /* Shopify's CDN sends access-control-allow-origin: *, and without this
         the canvas is tainted and toBlob throws a SecurityError — which is
         the whole design gone, at the last step, with nothing to show. */
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('load')); };
      img.src = src;
    });
  }

  function pick(i, file) {
    var st = S.slots[i];
    if (String(file.type).indexOf('image/') !== 0) {
      return fail('That file is not a picture. JPG, PNG or WebP please.');
    }
    if (file.size > UPLOAD_MAX) {
      return fail('That photo is over ' + F.max_mb + 'MB. Please choose a smaller one.');
    }
    fail('');

    var url = URL.createObjectURL(file);
    load(url).then(function (img) {
      st.img = img;
      st.name = file.name;
      st.zoom = 1; st.ox = 0; st.oy = 0;
      st.el.well.classList.add('is-set');
      st.el.ph.hidden = true;
      st.el.ctl.hidden = false;
      st.el.zoom.value = '1';
      paintCrop(i);
      touch();
    }).catch(function () {
      URL.revokeObjectURL(url);
      /* HEIC is in the accept list because iPhones offer it, but Safari is
         the only browser that will decode one. Say which photo failed. */
      fail('We could not open that photo. If it came from an iPhone, try ' +
           'sharing it as a JPG and choose it again.');
    });
  }

  function fail(msg) {
    if (!el.err) return;
    el.err.textContent = msg || '';
    el.err.hidden = !msg;
  }

  /* --------------------------------------------------------------- drawing
     One routine draws the on-screen preview, the little cropper in each well
     and the print file. Everything below is in fractions of the artwork, so
     the only difference between a 320px preview and a 1748px print file is
     what W and H are when it runs. That is the only way the card the customer
     approves and the card that prints are the same card. */

  function fit(slot, st, sw, sh) {
    /* Cover: fill the slot, crop the overflow, then the customer's zoom on
       top of that. Offsets are fractions of the slot, so they survive being
       re-applied at print resolution. */
    var iw = st.img.naturalWidth, ih = st.img.naturalHeight;
    var base = Math.max(sw / iw, sh / ih);
    var sc = base * st.zoom;
    var dw = iw * sc, dh = ih * sc;
    return {
      dw: dw, dh: dh,
      dx: (sw - dw) / 2 + st.ox * sw,
      dy: (sh - dh) / 2 + st.oy * sh
    };
  }

  function clampOffset(i) {
    /* Stop the photo being dragged clear of its own hole, which otherwise
       prints a wedge of card colour where a face should be. */
    var slot = T.slots[i], st = S.slots[i];
    if (!st.img) return;
    var sw = 1000, sh = sw * (slot.h * (T.print_h || 1)) / (slot.w * (T.print_w || 1));
    var f = fit(slot, st, sw, sh);
    var mx = Math.max(0, (f.dw - sw) / 2) / sw;
    var my = Math.max(0, (f.dh - sh) / 2) / sh;
    st.ox = Math.max(-mx, Math.min(mx, st.ox));
    st.oy = Math.max(-my, Math.min(my, st.oy));
  }

  function shape(ctx, kind, x, y, w, h) {
    var r;
    ctx.beginPath();
    if (kind === 'circle') {
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else if (kind === 'rounded') {
      r = Math.min(w, h) * 0.12;
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    } else if (kind === 'arch') {
      /* Flat bottom, half-round top — the shape of a window, which is what
         a photo on a card usually wants to be. */
      r = w / 2;
      ctx.moveTo(x, y + h);
      ctx.lineTo(x, y + r);
      ctx.arc(x + r, y + r, r, Math.PI, 0);
      ctx.lineTo(x + w, y + h);
      ctx.closePath();
    } else if (kind === 'heart') {
      /* Two curves meeting at a point at the bottom and a dip at the top.
         The control points run outside the box on purpose — that is what
         gives the lobes their width without the heart sitting small inside
         its own slot. Faces land in the upper two thirds, so the dip costs
         less of the photo than it looks like it should. */
      ctx.moveTo(x + w / 2, y + h);
      ctx.bezierCurveTo(x - w * 0.10, y + h * 0.62,
                        x + w * 0.14, y + h * 0.02,
                        x + w / 2,    y + h * 0.26);
      ctx.bezierCurveTo(x + w * 0.86, y + h * 0.02,
                        x + w * 1.10, y + h * 0.62,
                        x + w / 2,    y + h);
      ctx.closePath();
    } else {
      ctx.rect(x, y, w, h);
    }
  }

  function wrap(ctx, text, maxW) {
    var words = String(text).split(/\s+/).filter(Boolean);
    var lines = [], line = '';
    for (var i = 0; i < words.length; i++) {
      var test = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxW && line) {
        lines.push(line); line = words[i];
      } else { line = test; }
    }
    if (line) lines.push(line);
    return lines;
  }

  function drawText(ctx, line, val, W, H) {
    if (!val) return;
    var f = font(line.font);
    var px = (line.size || 0.06) * H;
    var maxW = (line.w || 0.8) * W;
    var txt = line.upper ? String(val).toUpperCase() : String(val);

    /* Shrink to fit rather than wrap, down to 62% — a name is meant to be one
       line, and a long one looking smaller reads better than it wrapping. Past
       that it wraps, because 62% of a heading is as small as it can go and
       still be the heading. */
    var size = px, lines;
    for (var n = 0; n < 14; n++) {
      ctx.font = f.weight + ' ' + size + 'px ' + f.stack;
      if (ctx.measureText(txt).width <= maxW || size <= px * 0.62) break;
      size = size * 0.94;
    }
    ctx.font = f.weight + ' ' + size + 'px ' + f.stack;
    lines = wrap(ctx, txt, maxW);

    ctx.fillStyle = line.colour || '#1A1A1A';
    ctx.textAlign = line.align || 'center';
    ctx.textBaseline = 'middle';
    if (line.spacing && 'letterSpacing' in ctx) {
      ctx.letterSpacing = (line.spacing * size).toFixed(2) + 'px';
    }

    var lh = size * (line.leading || 1.14);
    var x = (line.x != null ? line.x : 0.5) * W;
    var mid = (line.y != null ? line.y : 0.8) * H;
    var top = mid - ((lines.length - 1) * lh) / 2;
    for (var j = 0; j < lines.length; j++) {
      ctx.fillText(lines[j], x, top + j * lh);
    }
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  }

  function render(ctx, W, H) {
    ctx.save();
    ctx.fillStyle = T.paper || '#FFFDF8';
    ctx.fillRect(0, 0, W, H);

    var over = T.layers === 'photo-under-artwork';

    /* Artwork first when the photo sits on top of it, last when the artwork
       is a frame with a hole in it and the photo belongs behind. */
    if (!over && S.art) ctx.drawImage(S.art, 0, 0, W, H);

    T.slots.forEach(function (slot, i) {
      var st = S.slots[i];
      var x = slot.x * W, y = slot.y * H, w = slot.w * W, h = slot.h * H;

      if (!st.img) {
        /* An empty slot is drawn as a hole so the customer can see where the
           photo is going before they have chosen one. It never prints: the
           gate below will not let the design through with a slot empty. */
        ctx.save();
        shape(ctx, slot.shape, x, y, w, h);
        ctx.fillStyle = 'rgba(237,33,122,.07)';
        ctx.fill();
        ctx.lineWidth = Math.max(1, W * 0.004);
        ctx.setLineDash([W * 0.014, W * 0.010]);
        ctx.strokeStyle = 'rgba(237,33,122,.55)';
        ctx.stroke();
        ctx.restore();
        return;
      }

      ctx.save();
      shape(ctx, slot.shape, x, y, w, h);
      ctx.clip();
      var f = fit(slot, st, w, h);
      ctx.drawImage(st.img, x + f.dx, y + f.dy, f.dw, f.dh);
      ctx.restore();
    });

    if (over && S.art) ctx.drawImage(S.art, 0, 0, W, H);

    T.text.forEach(function (line, i) { drawText(ctx, line, S.text[i], W, H); });
    ctx.restore();
  }

  /* --------------------------------------------------------------- preview */

  function paint() {
    if (!el.cv) return;
    var css = el.cv.clientWidth || 320;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var W = Math.round(css * dpr);
    var H = Math.round(W * (T.print_h / T.print_w));
    if (el.cv.width !== W || el.cv.height !== H) {
      el.cv.width = W; el.cv.height = H;
    }
    var ctx = el.cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    render(ctx, W, H);
  }

  function paintCrop(i) {
    var slot = T.slots[i], st = S.slots[i];
    if (!st.img) return;
    var cv = st.el.crop;
    var css = cv.clientWidth || st.el.well.clientWidth || 300;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var ar = (slot.h * T.print_h) / (slot.w * T.print_w);
    var W = Math.round(css * dpr), H = Math.round(W * ar);
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    var ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    ctx.save();
    shape(ctx, slot.shape, 0, 0, W, H);
    ctx.clip();
    var f = fit(slot, st, W, H);
    ctx.drawImage(st.img, f.dx, f.dy, f.dw, f.dh);
    ctx.restore();
    /* A hairline on the shape edge, so a pale photo still shows where the
       crop is. */
    ctx.save();
    shape(ctx, slot.shape, 0.5, 0.5, W - 1, H - 1);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(30,26,28,.18)';
    ctx.stroke();
    ctx.restore();
  }

  function dragable(i) {
    var st = S.slots[i];
    var cv = st.el.crop;
    var from = null;

    cv.addEventListener('pointerdown', function (e) {
      if (!st.img) return;
      /* The canvas is inside the <label>, so a plain click would re-open the
         file picker mid-drag. */
      e.preventDefault();
      from = { x: e.clientX, y: e.clientY, ox: st.ox, oy: st.oy,
               w: cv.clientWidth || 1, h: cv.clientHeight || 1 };
      if (cv.setPointerCapture) cv.setPointerCapture(e.pointerId);
    });

    cv.addEventListener('pointermove', function (e) {
      if (!from) return;
      st.ox = from.ox + (e.clientX - from.x) / from.w;
      st.oy = from.oy + (e.clientY - from.y) / from.h;
      clampOffset(i);
      paintCrop(i);
      paint();
    });

    ['pointerup', 'pointercancel'].forEach(function (ev) {
      cv.addEventListener(ev, function () {
        if (!from) return;
        from = null;
        touch();
      });
    });

    /* A click that was a drag must not fall through to the file picker. */
    cv.addEventListener('click', function (e) {
      if (st.img) { e.preventDefault(); e.stopPropagation(); }
    });
  }

  /* ------------------------------------------------------------- composite */

  function ready() {
    /* Every slot filled. Words are allowed to be empty — a template may have
       none, and a blank name line is a legitimate choice. */
    for (var i = 0; i < S.slots.length; i++) {
      if (!S.slots[i].img) return false;
    }
    return true;
  }

  function encode(cv) {
    /* Quality first, then quality again, then size. Dropping resolution is
       last because at A5 the lettering is what shows it. */
    return new Promise(function (resolve, reject) {
      cv.toBlob(function (b1) {
        if (!b1) return reject(new Error('encode'));
        if (b1.size <= MAX_BYTES) return resolve(b1);
        cv.toBlob(function (b2) {
          if (!b2) return reject(new Error('encode'));
          if (b2.size <= MAX_BYTES || cv.width <= MIN_WIDTH) return resolve(b2);
          var w = MIN_WIDTH, h = Math.round(w * cv.height / cv.width);
          var small = document.createElement('canvas');
          small.width = w; small.height = h;
          var sc = small.getContext('2d');
          sc.fillStyle = T.paper || '#FFFDF8';
          sc.fillRect(0, 0, w, h);
          sc.drawImage(cv, 0, 0, w, h);
          small.toBlob(function (b3) {
            b3 ? resolve(b3) : reject(new Error('encode'));
          }, 'image/jpeg', 0.9);
        }, 'image/jpeg', 0.88);
      }, 'image/jpeg', 0.95);
    });
  }

  function fonts() {
    /* The canvas draws with whatever face is resolved at the moment fillText
       runs. If the webfont has not arrived the print file gets the fallback
       and looks nothing like the preview, so wait for the faces this template
       actually asks for. */
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var jobs = T.text.map(function (line) {
      var f = font(line.font);
      return document.fonts.load(f.weight + ' 100px ' + f.stack).catch(function () {});
    });
    return Promise.all(jobs).catch(function () {});
  }

  function slug(s) {
    return String(s || 'card').toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'card';
  }

  function inject() {
    if (S.busy) return Promise.resolve(false);
    if (!ready()) return Promise.resolve(false);
    S.busy = true;
    el.wait.hidden = false;

    return fonts().then(function () {
      var cv = document.createElement('canvas');
      cv.width = T.print_w; cv.height = T.print_h;
      render(cv.getContext('2d'), T.print_w, T.print_h);
      return encode(cv);
    }).then(function (b) {
      /* The words go in the filename as well as the artwork. The bakery's
         picking list shows a file name; nobody should have to open the image
         to know whose card it is. */
      var words = S.text.filter(Boolean).join(' ');
      var name = 'cake-card-' + slug(T.id || T.name) +
                 (words ? '-' + slug(words) : '') + '.jpg';
      var file = new File([b], name, { type: 'image/jpeg' });
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      S.stale = false;
      S.busy = false;
      el.wait.hidden = true;
      fail('');
      return true;
    }).catch(function () {
      S.busy = false;
      el.wait.hidden = true;
      fail('Something went wrong putting your card together. Nudge the ' +
           'zoom slider to try again, and tell us if it keeps happening.');
      return false;
    });
  }

  function touch() {
    S.stale = true;
    paint();
    if (S.settle) clearTimeout(S.settle);
    S.settle = setTimeout(function () { inject(); }, SETTLE);
  }

  /* ------------------------------------------------------------- the gate
     validate() cannot tell a current composite from a stale one, and step()
     is synchronous, so there is no hook to await. This listens on the capture
     phase — ahead of the flow's own delegated handler on [data-mb-flow] —
     holds the click, finishes the composite, then re-fires it.

     S.pass is what stops that re-fire being caught here again. */

  function onStep4() {
    var s = document.querySelector('.mb-screen[data-step="4"]');
    return !!(s && s.classList.contains('is-on'));
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('#mb-next');
    if (!btn || !onStep4()) return;
    if (S.pass) { S.pass = false; return; }

    /* A slot still empty is our error to report, not the flow's — it would
       say "add the photo that goes on the front of the card", which is not
       what is missing when there are three holes and one is filled. */
    if (!ready()) {
      e.preventDefault(); e.stopPropagation();
      fail(S.slots.length > 1
        ? 'Add a photo to every space on the card to carry on.'
        : 'Add the photo that goes on the front of the card.');
      return;
    }

    if (!S.stale && !S.busy) return;

    e.preventDefault();
    e.stopPropagation();
    if (S.settle) clearTimeout(S.settle);

    var go = function () {
      inject().then(function (ok) {
        if (!ok) return;
        S.pass = true;
        btn.click();
      });
    };
    /* If a composite is already mid-flight, let it land first rather than
       starting a second one on top of it. */
    if (S.busy) {
      var wait = setInterval(function () {
        if (S.busy) return;
        clearInterval(wait);
        go();
      }, 120);
    } else { go(); }
  }, true);

  /* ----------------------------------------------------------------- boot */

  build();
  paint();

  load(T.preview || T.artwork).then(function (img) {
    S.art = img;
    paint();
  }).catch(function () {
    fail('We could not load this card design. Please refresh the page, and ' +
         'let us know if it happens again.');
  });

  /* The preview is sized in CSS, so it changes with the window. */
  var rz;
  window.addEventListener('resize', function () {
    if (rz) clearTimeout(rz);
    rz = setTimeout(function () {
      paint();
      S.slots.forEach(function (st, i) { if (st.img) paintCrop(i); });
    }, 150);
  });
})();
